import { useEffect, useRef } from 'react';

import { ACADEMIC_TYPE } from '@common/type/CourseTypes';

import { createGoogleAnalyticItem, sendPageViewContentCourseDetail } from '@helpers/googleTagManager';
import { AcademicCourseEnrollmentStatus, CourseDetailEntity } from '@redux/course/type/courseDetailType';

type UseCourseAcademicBannerProps = {
  courseDetailData: CourseDetailEntity;
  enrollmentStatus: AcademicCourseEnrollmentStatus;
  courseName: string;
};

const useCourseAcademicBanner = ({ courseDetailData, courseName, enrollmentStatus }: UseCourseAcademicBannerProps) => {
  const isSendGtmEvent = useRef<boolean>(false);
  const isUnPurchase = [AcademicCourseEnrollmentStatus.UNPUBLISHED, AcademicCourseEnrollmentStatus.PUBLISHED].includes(
    enrollmentStatus,
  );

  const isEnrolled = [
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLED,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PENDING,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED,
  ].includes(enrollmentStatus);

  const isUnAvailable = isUnPurchase || isEnrolled;

  const isGtmAuditCredit = enrollmentStatus === AcademicCourseEnrollmentStatus.AVAILABLE_REGISTER;
  const isGtmUpgrade = enrollmentStatus === AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLED;
  const isGtmCredit = [
    AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLMENT_EXPIRED,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED_REGRADE,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_REGRADE,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLMENT_EXPIRED,
  ].includes(enrollmentStatus);

  const getGa4Item = () => {
    if (isGtmAuditCredit) {
      return ([ACADEMIC_TYPE.AUDIT, ACADEMIC_TYPE.CREDIT] as (
        | ACADEMIC_TYPE.AUDIT
        | ACADEMIC_TYPE.CREDIT
      )[]).map((academicType) =>
        createGoogleAnalyticItem({ courseDetail: courseDetailData, academicType, courseName }),
      );
    }

    if (isGtmCredit) {
      return [
        createGoogleAnalyticItem({
          courseDetail: courseDetailData,
          academicType: ACADEMIC_TYPE.CREDIT,
          courseName,
        }),
      ];
    }

    if (isGtmUpgrade) {
      return [
        createGoogleAnalyticItem({
          courseDetail: courseDetailData,
          academicType: ACADEMIC_TYPE.UPGRADE,
          courseName,
        }),
      ];
    }
  };

  const bannerPrimaryColor = courseDetailData?.bannerPrimaryColor;
  const bannerSecondaryColor = courseDetailData?.bannerSecondaryColor;

  useEffect(() => {
    if (isUnPurchase || isEnrolled || isSendGtmEvent.current || !courseDetailData) return;

    const ga4Items = getGa4Item();

    if (!ga4Items) return;

    isSendGtmEvent.current = true;

    const value = ga4Items.reduce((total, item) => total + item.price, 0);

    sendPageViewContentCourseDetail({
      currency: 'THB',
      value,
      items: ga4Items,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnPurchase, isEnrolled, courseDetailData]);

  return {
    isUnAvailable,
    isEnrolled,
    isUnPurchase,
    bannerPrimaryColor,
    bannerSecondaryColor,
  };
};

export default useCourseAcademicBanner;
